import { createTheme } from '@mui/material/styles';

// Custom breakpoints based on Project Lighthouse requirements
// Minimum screen size support: 320px
// Mobile-first approach for home health supervision application
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 320,   // Mobile phones (min requirement) - 320px to 599px
      sm: 600,   // Large phones/small tablets - 600px to 899px  
      md: 900,   // Tablets/small desktops - 900px to 1199px
      lg: 1200,  // Desktop/laptops - 1200px to 1535px
      xl: 1536   // Large desktop/monitors - 1536px+
    },
  },
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    // Fluid typography using CSS clamp() for smooth scaling
    // Format: clamp(min-size, preferred-size, max-size)
    // Preferred size uses viewport width (vw) for smooth scaling
    
    h1: {
      // Scales from 1.5rem (24px) to 2.5rem (40px) smoothly
      fontSize: 'clamp(1.5rem, 1.2rem + 1.3vw, 2.5rem)',
      fontWeight: 500,
      lineHeight: 'clamp(1.2, 1.15 + 0.05vw, 1.25)',
      letterSpacing: '-0.01em',
      '@media (min-width:600px)': {
        letterSpacing: '-0.02em', // Tighter spacing on larger screens
      },
    },
    h2: {
      // Scales from 1.25rem (20px) to 2rem (32px) smoothly
      fontSize: 'clamp(1.25rem, 1rem + 1vw, 2rem)',
      fontWeight: 500,
      lineHeight: 'clamp(1.3, 1.25 + 0.05vw, 1.35)',
      letterSpacing: '-0.005em',
    },
    h3: {
      // Scales from 1.125rem (18px) to 1.5rem (24px) smoothly
      fontSize: 'clamp(1.125rem, 0.9rem + 0.7vw, 1.5rem)',
      fontWeight: 500,
      lineHeight: 'clamp(1.3, 1.25 + 0.05vw, 1.35)',
      letterSpacing: '0em',
    },
    h4: {
      // Scales from 1rem (16px) to 1.25rem (20px) smoothly
      fontSize: 'clamp(1rem, 0.85rem + 0.5vw, 1.25rem)',
      fontWeight: 500,
      lineHeight: 'clamp(1.4, 1.35 + 0.05vw, 1.45)',
      letterSpacing: '0.005em',
    },
    h5: {
      // Scales from 0.875rem (14px) to 1rem (16px) smoothly
      fontSize: 'clamp(0.875rem, 0.8rem + 0.25vw, 1rem)',
      fontWeight: 500,
      lineHeight: 'clamp(1.4, 1.35 + 0.05vw, 1.45)',
      letterSpacing: '0.01em',
    },
    h6: {
      // Subtle scaling for h6, primarily used for smaller headings
      fontSize: 'clamp(0.875rem, 0.85rem + 0.1vw, 0.9rem)',
      fontWeight: 500,
      lineHeight: 'clamp(1.4, 1.35 + 0.05vw, 1.45)',
      letterSpacing: '0.01em',
    },
    
    // Body text with optimal reading experience
    body1: {
      // Scales from 0.875rem (14px) to 1rem (16px) for optimal readability
      fontSize: 'clamp(0.875rem, 0.8rem + 0.25vw, 1rem)',
      lineHeight: 'clamp(1.5, 1.45 + 0.1vw, 1.6)',
      letterSpacing: '0.00938em',
      // Removed maxWidth constraint to allow full layout utilization
    },
    body2: {
      // Scales from 0.75rem (12px) to 0.875rem (14px) for secondary text
      fontSize: 'clamp(0.75rem, 0.7rem + 0.2vw, 0.875rem)',
      lineHeight: 'clamp(1.5, 1.45 + 0.1vw, 1.6)',
      letterSpacing: '0.00714em',
    },
    
    // Specialized typography variants
    subtitle1: {
      // For larger secondary text
      fontSize: 'clamp(1rem, 0.9rem + 0.3vw, 1.125rem)',
      fontWeight: 400,
      lineHeight: 'clamp(1.5, 1.45 + 0.1vw, 1.6)',
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      // For smaller secondary text
      fontSize: 'clamp(0.875rem, 0.8rem + 0.2vw, 0.9375rem)',
      fontWeight: 500,
      lineHeight: 'clamp(1.5, 1.45 + 0.1vw, 1.6)',
      letterSpacing: '0.00714em',
    },
    
    // Button text optimized for touch and readability
    button: {
      fontSize: 'clamp(0.875rem, 0.8rem + 0.25vw, 1rem)',
      fontWeight: 500,
      textTransform: 'none',
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      // Ensure button text is always readable
      '@media (max-width:599px)': {
        fontWeight: 600, // Slightly bolder on mobile for better visibility
      },
    },
    
    // Caption and small text
    caption: {
      // For very small text like form helpers, labels
      fontSize: 'clamp(0.75rem, 0.7rem + 0.15vw, 0.8125rem)',
      fontWeight: 400,
      lineHeight: 'clamp(1.6, 1.55 + 0.1vw, 1.7)',
      letterSpacing: '0.03333em',
    },
    
    // Overline for labels and small headings
    overline: {
      fontSize: 'clamp(0.625rem, 0.6rem + 0.1vw, 0.6875rem)',
      fontWeight: 600,
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },
  spacing: 8, // Base spacing unit for consistent layouts
  components: {
    // Global CSS baseline for mobile-first approach
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          // Prevent horizontal scrolling on mobile
          overflowX: 'hidden',
          // Improve text rendering
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        body: {
          // Prevent horizontal scrolling
          overflowX: 'hidden',
          // Mobile-first body styles
          minHeight: '100vh',
          '@media (max-width:599px)': {
            fontSize: '14px',
            lineHeight: 1.5,
          },
        },
        // Comprehensive responsive images with modern optimizations
        img: {
          // Basic responsive behavior
          maxWidth: '100%',
          height: 'auto',
          
          // Performance optimizations
          display: 'block',
          
          // Modern object-fit support for better scaling
          objectFit: 'cover',
          objectPosition: 'center',
          
          // Smooth loading transition
          transition: 'opacity 0.3s ease-in-out',
          
          // High-quality rendering
          imageRendering: 'optimizeQuality',
          
          // Prevent layout shift during loading
          '&[width]': {
            width: 'auto',
            maxWidth: '100%',
          },
          
          // Lazy loading optimization
          '&[loading="lazy"]': {
            opacity: 0,
            '&[data-loaded="true"]': {
              opacity: 1,
            },
          },
          
          // Responsive image variants
          '&.img-fluid': {
            maxWidth: '100%',
            height: 'auto',
          },
          
          '&.img-cover': {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
          },
          
          '&.img-contain': {
            objectFit: 'contain',
            width: '100%',
            height: '100%',
          },
          
          '&.img-fill': {
            objectFit: 'fill',
            width: '100%',
            height: '100%',
          },
          
          // Aspect ratio containers for responsive images
          '&.aspect-square': {
            aspectRatio: '1 / 1',
          },
          
          '&.aspect-video': {
            aspectRatio: '16 / 9',
          },
          
          '&.aspect-photo': {
            aspectRatio: '4 / 3',
          },
          
          // High DPI screen optimizations
          '@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)': {
            imageRendering: 'optimizeQuality',
          },
          
          // Mobile-specific optimizations
          '@media (max-width:599px)': {
            // Ensure touch-friendly interaction
            touchAction: 'manipulation',
            // Prevent accidental image drag on mobile
            WebkitUserDrag: 'none',
            userSelect: 'none',
          },
          
          // Dark mode compatibility
          '@media (prefers-color-scheme: dark)': {
            // Slightly reduce brightness for dark themes if needed
            '&.img-auto-contrast': {
              filter: 'brightness(0.9)',
            },
          },
          
          // Print media optimizations
          '@media print': {
            maxWidth: '100% !important',
            height: 'auto !important',
            pageBreakInside: 'avoid',
          },
        },
        
        // Enhanced picture element support
        picture: {
          display: 'block',
          
          '& img': {
            width: '100%',
            height: 'auto',
          },
          
          // Mobile-first responsive pictures
          '@media (max-width:599px)': {
            '& img': {
              maxWidth: '100%',
            },
          },
        },
        
        // SVG optimization for icons and graphics
        svg: {
          // Responsive SVG behavior
          maxWidth: '100%',
          height: 'auto',
          
          // Improve rendering quality
          shapeRendering: 'geometricPrecision',
          
          // Icon-specific styles
          '&.icon': {
            display: 'inline-block',
            verticalAlign: 'middle',
            fill: 'currentColor',
          },
          
          // Logo and large graphics
          '&.logo': {
            maxHeight: '100%',
            width: 'auto',
          },
        },
        
        // CSS for responsive image utilities
        '.responsive-img-container': {
          position: 'relative',
          overflow: 'hidden',
          
          '& img': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          },
          
          // Aspect ratio variants
          '&.ratio-16-9': {
            paddingBottom: '56.25%', // 9/16 * 100%
          },
          
          '&.ratio-4-3': {
            paddingBottom: '75%', // 3/4 * 100%
          },
          
          '&.ratio-1-1': {
            paddingBottom: '100%',
          },
          
          '&.ratio-3-2': {
            paddingBottom: '66.67%', // 2/3 * 100%
          },
        },
        
        // Loading placeholder styles
        '.img-placeholder': {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          
          '&::before': {
            content: '"Loading..."',
            color: 'rgba(0, 0, 0, 0.5)',
            fontSize: '14px',
          },
          
          // Skeleton loading animation
          '&.skeleton': {
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeleton-loading 1.5s infinite',
            
            '&::before': {
              display: 'none',
            },
          },
        },
        
        // Keyframe for skeleton loading
        '@keyframes skeleton-loading': {
          '0%': {
            backgroundPosition: '200% 0',
          },
          '100%': {
            backgroundPosition: '-200% 0',
          },
        },
        // Mobile-friendly table defaults
        table: {
          '@media (max-width:599px)': {
            fontSize: '0.75rem',
            '& th, & td': {
              padding: '8px 4px',
            },
          },
        },
      },
    },

    // Container component optimized for mobile-first
    MuiContainer: {
      styleOverrides: {
        root: {
          // Mobile-first padding
          paddingLeft: '8px',
          paddingRight: '8px',
          '@media (min-width:600px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
          '@media (min-width:900px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            paddingLeft: '32px',
            paddingRight: '32px',
            // Enhanced max-width for better content layout
            '&.MuiContainer-maxWidthLg': {
              maxWidth: '1280px',
            },
            '&.MuiContainer-maxWidthXl': {
              maxWidth: '1400px',
            },
            // Professional desktop container styling
            '&.MuiContainer-fixed': {
              paddingLeft: '40px',
              paddingRight: '40px',
            },
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            paddingLeft: '40px',
            paddingRight: '40px',
            // Ultra-wide screen container optimization
            '&.MuiContainer-maxWidthLg': {
              maxWidth: '1400px',
            },
            '&.MuiContainer-maxWidthXl': {
              maxWidth: '1600px',
            },
            // Premium spacing for ultra-wide screens
            '&.MuiContainer-fixed': {
              paddingLeft: '48px',
              paddingRight: '48px',
            },
            // Content-aware max-width for optimal reading
            '&[data-content-type="text"]': {
              maxWidth: '1200px',
            },
            '&[data-content-type="dashboard"]': {
              maxWidth: '1800px',
            },
          },
        },
      },
    },

    // AppBar optimized for mobile and responsive navigation
    MuiAppBar: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            minHeight: '56px',
            '& .MuiToolbar-root': {
              minHeight: '56px',
              padding: '0 8px',
            },
          },
          // Enhanced responsiveness for different screen sizes
          '@media (min-width:600px) and (max-width:899px)': {
            '& .MuiToolbar-root': {
              padding: '0 16px',
            },
          },
        },
      },
    },

    // Toolbar mobile optimization
    MuiToolbar: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            minHeight: '56px',
            padding: '0 8px',
            '& .MuiTypography-h6': {
              fontSize: '1rem',
            },
          },
        },
      },
    },

    // Mobile-first button styling with tablet enhancements
    MuiButton: {
      styleOverrides: {
        root: {
          // Touch-friendly minimum size
          minHeight: '44px',
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          '@media (max-width:599px)': {
            minHeight: '48px',
            fontSize: '0.875rem',
            padding: '12px 16px',
            // Full-width by default on mobile for primary actions
            '&.MuiButton-containedPrimary': {
              minHeight: '52px',
            },
          },
          // Tablet-specific optimizations (sm: 600-899px)
          '@media (min-width:600px) and (max-width:899px)': {
            minHeight: '46px',
            fontSize: '0.9375rem',
            padding: '11px 18px',
            borderRadius: '10px',
            // Enhanced spacing for tablet touch interaction
            '&.MuiButton-containedPrimary': {
              minHeight: '48px',
              fontWeight: 600,
            },
            // Improved button groups on tablet
            '&.MuiButton-group': {
              margin: '0 4px',
            },
          },
          // Medium tablet and up (md: 900-1199px)
          '@media (min-width:900px) and (max-width:1199px)': {
            minHeight: '44px',
            padding: '10px 20px',
            fontSize: '1rem',
            borderRadius: '12px',
            // Enhanced hover states for larger tablets
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
              transition: 'all 0.2s ease-in-out',
            },
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            minHeight: '42px',
            padding: '8px 24px',
            fontSize: '1rem',
            borderRadius: '8px',
            fontWeight: 500,
            letterSpacing: '0.02em',
            // Professional desktop hover effects
            transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            // Enhanced primary button styling for desktop
            '&.MuiButton-containedPrimary': {
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                boxShadow: '0 8px 20px rgba(25, 118, 210, 0.4)',
              },
            },
            // Enhanced secondary button styling
            '&.MuiButton-outlined': {
              borderWidth: '1.5px',
              '&:hover': {
                borderWidth: '1.5px',
                background: 'rgba(25, 118, 210, 0.04)',
              },
            },
            // Improved button spacing in groups
            '&.MuiButton-group': {
              margin: '0 6px',
            },
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            minHeight: '44px',
            padding: '10px 28px',
            fontSize: '1.0625rem',
            borderRadius: '10px',
            fontWeight: 500,
            letterSpacing: '0.025em',
            // Ultra-wide screen enhancements
            '&.MuiButton-containedPrimary': {
              minHeight: '48px',
              padding: '12px 32px',
              fontSize: '1.125rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              boxShadow: '0 3px 12px rgba(25, 118, 210, 0.35)',
              '&:hover': {
                boxShadow: '0 10px 24px rgba(25, 118, 210, 0.45)',
                transform: 'translateY(-3px)',
              },
            },
            // Enhanced spacing for ultra-wide layouts
            '&.MuiButton-group': {
              margin: '0 8px',
            },
                     },
         },
       },
    },

    // Icon buttons with touch-friendly targets and tablet enhancements
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: '44px',
          minHeight: '44px',
          '@media (max-width:599px)': {
            minWidth: '48px',
            minHeight: '48px',
            // Larger touch targets on mobile
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem',
            },
          },
          // Tablet-specific icon button sizing (sm: 600-899px)
          '@media (min-width:600px) and (max-width:899px)': {
            minWidth: '46px',
            minHeight: '46px',
            borderRadius: '12px',
            '& .MuiSvgIcon-root': {
              fontSize: '1.375rem',
            },
            '&:hover': {
              transform: 'scale(1.05)',
              transition: 'transform 0.1s ease-in-out',
            },
          },
          // Medium tablet icon button styling (md: 900-1199px)
          '@media (min-width:900px) and (max-width:1199px)': {
            minWidth: '44px',
            minHeight: '44px',
            borderRadius: '14px',
            '& .MuiSvgIcon-root': {
              fontSize: '1.25rem',
            },
            '&:hover': {
              transform: 'scale(1.08)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            },
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            minWidth: '40px',
            minHeight: '40px',
            borderRadius: '8px',
            transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
            '& .MuiSvgIcon-root': {
              fontSize: '1.125rem',
            },
            '&:hover': {
              transform: 'scale(1.1)',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
            },
            '&:active': {
              transform: 'scale(1.05)',
            },
            // Enhanced primary icon buttons for desktop
            '&.MuiIconButton-colorPrimary': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
              },
            },
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            minWidth: '42px',
            minHeight: '42px',
            borderRadius: '10px',
            '& .MuiSvgIcon-root': {
              fontSize: '1.25rem',
            },
            '&:hover': {
              transform: 'scale(1.12)',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
            },
            // Premium styling for ultra-wide screens
            '&.MuiIconButton-colorPrimary': {
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.3)',
              },
            },
          },
        },
      },
    },

    // Mobile navigation drawer styling
    MuiDrawer: {
      styleOverrides: {
        paper: {
          // Smooth transitions for mobile drawer
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (max-width:599px)': {
            width: '280px',
            maxWidth: '85vw', // Ensure drawer doesn't take full width on very small screens
          },
        },
      },
    },

    // List styling for navigation drawer
    MuiList: {
      styleOverrides: {
        root: {
          // Navigation list styling
          '&[data-navigation="true"]': {
            '& .MuiListItemButton-root': {
              minHeight: '48px',
              borderRadius: '8px',
              margin: '2px 8px',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'translateX(4px)',
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'inherit',
                },
              },
            },
            '& .MuiListItemIcon-root': {
              minWidth: '40px',
              color: 'text.secondary',
            },
            '& .MuiListItemText-primary': {
              fontWeight: 500,
            },
          },
        },
      },
    },

    // Avatar styling for user info in drawer
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: '40px',
          height: '40px',
          fontSize: '1rem',
          fontWeight: 600,
          '@media (max-width:599px)': {
            width: '44px',
            height: '44px',
            fontSize: '1.1rem',
          },
        },
      },
    },

    // Tabs optimized for mobile scrolling
    MuiTabs: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            minHeight: '48px',
            '& .MuiTabs-indicator': {
              height: '3px',
            },
          },
        },
        flexContainer: {
          '@media (max-width:599px)': {
            // Allow horizontal scrolling on mobile
            flexWrap: 'nowrap',
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minWidth: 'auto',
          '@media (max-width:599px)': {
            minHeight: '48px',
            padding: '12px 8px',
            fontSize: '0.75rem',
            minWidth: '80px',
            // Compact tab layout for mobile
            '& .MuiSvgIcon-root': {
              fontSize: '1.25rem',
              marginBottom: '4px',
            },
          },
          '@media (min-width:600px)': {
            minHeight: '48px',
            padding: '12px 16px',
          },
        },
      },
    },

    // Grid system optimized for responsive layouts
    MuiGrid: {
      styleOverrides: {
        container: {
          // Base container styles (mobile-first)
          width: '100%',
          boxSizing: 'border-box',
          
          // Mobile spacing (xs: 320-599px)
          '@media (max-width:599px)': {
            padding: '8px',
            margin: '0 -4px',
            '& .MuiGrid-item': {
              padding: '4px',
            },
            // Compact spacing variant for tight layouts
            '&.MuiGrid-spacing-compact': {
              padding: '4px',
              margin: '0 -2px',
              '& .MuiGrid-item': {
                padding: '2px',
              },
            },
            // Wide spacing variant for breathing room
            '&.MuiGrid-spacing-wide': {
              padding: '12px',
              margin: '0 -6px',
              '& .MuiGrid-item': {
                padding: '6px',
              },
            },
          },
          
          // Small tablet spacing (sm: 600-899px)  
          '@media (min-width:600px)': {
            padding: '16px',
            margin: '0 -8px',
            '& .MuiGrid-item': {
              padding: '8px',
            },
            // Compact spacing variant
            '&.MuiGrid-spacing-compact': {
              padding: '8px',
              margin: '0 -4px',
              '& .MuiGrid-item': {
                padding: '4px',
              },
            },
            // Wide spacing variant
            '&.MuiGrid-spacing-wide': {
              padding: '24px',
              margin: '0 -12px',
              '& .MuiGrid-item': {
                padding: '12px',
              },
            },
          },
          
          // Medium screens and up (md: 900px+)
          '@media (min-width:900px)': {
            padding: '24px',
            margin: '0 -12px',
            '& .MuiGrid-item': {
              padding: '12px',
            },
            // Compact spacing variant
            '&.MuiGrid-spacing-compact': {
              padding: '12px',
              margin: '0 -6px',
              '& .MuiGrid-item': {
                padding: '6px',
              },
            },
            // Wide spacing variant  
            '&.MuiGrid-spacing-wide': {
              padding: '32px',
              margin: '0 -16px',
              '& .MuiGrid-item': {
                padding: '16px',
              },
            },
          },

          // Large screens and up (lg: 1200px+)
          '@media (min-width:1200px)': {
            // Enhanced spacing for desktop
            '&.MuiGrid-spacing-wide': {
              padding: '40px',
              margin: '0 -20px',
              '& .MuiGrid-item': {
                padding: '20px',
              },
            },
          },

          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            // Professional desktop grid spacing
            padding: '32px',
            margin: '0 -16px',
            '& .MuiGrid-item': {
              padding: '16px',
            },
            // Enhanced wide spacing for desktop layouts
            '&.MuiGrid-spacing-wide': {
              padding: '48px',
              margin: '0 -24px',
              '& .MuiGrid-item': {
                padding: '24px',
              },
            },
            // Desktop dashboard optimization
            '&.MuiGrid-pattern-dashboard': {
              '& .MuiGrid-item[data-dashboard-size="metric"]': {
                flexBasis: '20%',
                maxWidth: '20%',
              },
              '& .MuiGrid-item[data-dashboard-size="chart-large"]': {
                flexBasis: '75%',
                maxWidth: '75%',
              },
              '& .MuiGrid-item[data-dashboard-size="chart-small"]': {
                flexBasis: '25%',
                maxWidth: '25%',
              },
            },
          },

          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            // Premium ultra-wide grid spacing
            padding: '40px',
            margin: '0 -20px',
            '& .MuiGrid-item': {
              padding: '20px',
            },
            // Ultra-wide spacing for premium layouts
            '&.MuiGrid-spacing-wide': {
              padding: '56px',
              margin: '0 -28px',
              '& .MuiGrid-item': {
                padding: '28px',
              },
            },
            // Ultra-wide dashboard optimization
            '&.MuiGrid-pattern-dashboard': {
              '& .MuiGrid-item[data-dashboard-size="metric"]': {
                flexBasis: '16.666667%',
                maxWidth: '16.666667%',
              },
              '& .MuiGrid-item[data-dashboard-size="chart-large"]': {
                flexBasis: '66.666667%',
                maxWidth: '66.666667%',
              },
              '& .MuiGrid-item[data-dashboard-size="chart-medium"]': {
                flexBasis: '50%',
                maxWidth: '50%',
              },
              '& .MuiGrid-item[data-dashboard-size="chart-small"]': {
                flexBasis: '33.333333%',
                maxWidth: '33.333333%',
              },
            },
            // Ultra-wide gallery with more columns
            '&.MuiGrid-pattern-gallery': {
              '& .MuiGrid-item': {
                flexBasis: '20%',
                maxWidth: '20%',
              },
            },
          },

          // SPECIALIZED GRID PATTERNS

          // Form grid pattern - optimized for form layouts
          '&.MuiGrid-pattern-form': {
            '@media (max-width:599px)': {
              '& .MuiGrid-item': {
                // Full width on mobile for better touch interaction
                flexBasis: '100%',
                maxWidth: '100%',
              },
            },
            '@media (min-width:600px)': {
              '& .MuiGrid-item[data-grid-size="half"]': {
                flexBasis: '50%',
                maxWidth: '50%',
              },
              '& .MuiGrid-item[data-grid-size="third"]': {
                flexBasis: '33.333333%',
                maxWidth: '33.333333%',
              },
              '& .MuiGrid-item[data-grid-size="quarter"]': {
                flexBasis: '25%',
                maxWidth: '25%',
              },
            },
          },

          // Dashboard grid pattern - optimized for cards and widgets
          '&.MuiGrid-pattern-dashboard': {
            '@media (max-width:599px)': {
              '& .MuiGrid-item': {
                flexBasis: '100%',
                maxWidth: '100%',
              },
            },
            '@media (min-width:600px) and (max-width:899px)': {
              '& .MuiGrid-item[data-dashboard-size="metric"]': {
                flexBasis: '50%',
                maxWidth: '50%',
              },
              '& .MuiGrid-item[data-dashboard-size="chart"]': {
                flexBasis: '100%',
                maxWidth: '100%',
              },
            },
            '@media (min-width:900px)': {
              '& .MuiGrid-item[data-dashboard-size="metric"]': {
                flexBasis: '25%',
                maxWidth: '25%',
              },
              '& .MuiGrid-item[data-dashboard-size="chart-large"]': {
                flexBasis: '66.666667%',
                maxWidth: '66.666667%',
              },
              '& .MuiGrid-item[data-dashboard-size="chart-small"]': {
                flexBasis: '33.333333%',
                maxWidth: '33.333333%',
              },
            },
          },

          // Content grid pattern - optimized for text and media content
          '&.MuiGrid-pattern-content': {
            '@media (min-width:900px)': {
              '& .MuiGrid-item[data-content-type="main"]': {
                flexBasis: '66.666667%',
                maxWidth: '66.666667%',
              },
              '& .MuiGrid-item[data-content-type="sidebar"]': {
                flexBasis: '33.333333%',
                maxWidth: '33.333333%',
              },
            },
            '@media (max-width:899px)': {
              '& .MuiGrid-item[data-content-type="main"], & .MuiGrid-item[data-content-type="sidebar"]': {
                flexBasis: '100%',
                maxWidth: '100%',
              },
            },
          },

          // Gallery grid pattern - optimized for image/card galleries
          '&.MuiGrid-pattern-gallery': {
            '@media (max-width:599px)': {
              '& .MuiGrid-item': {
                flexBasis: '100%',
                maxWidth: '100%',
              },
            },
            '@media (min-width:600px) and (max-width:899px)': {
              '& .MuiGrid-item': {
                flexBasis: '50%',
                maxWidth: '50%',
              },
            },
            '@media (min-width:900px) and (max-width:1199px)': {
              '& .MuiGrid-item': {
                flexBasis: '33.333333%',
                maxWidth: '33.333333%',
              },
            },
            '@media (min-width:1200px)': {
              '& .MuiGrid-item': {
                flexBasis: '25%',
                maxWidth: '25%',
              },
            },
          },

          // AUTO-SIZING UTILITIES

          // Auto-fit pattern - automatically fits items based on min-width
          '&.MuiGrid-autofit': {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            
            '@media (max-width:599px)': {
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '8px',
            },
            '@media (min-width:900px)': {
              gap: '24px',
            },
          },

          // Auto-fill pattern - fills space with items of specified min-width
          '&.MuiGrid-autofill': {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '16px',
            
            '@media (max-width:599px)': {
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '8px',
            },
            '@media (min-width:900px)': {
              gap: '24px',
            },
          },

          // FLEX UTILITIES

          // Equal height items
          '&.MuiGrid-equalHeight': {
            '& .MuiGrid-item': {
              display: 'flex',
              '& > *': {
                flex: 1,
              },
            },
          },

          // Center alignment
          '&.MuiGrid-center': {
            justifyContent: 'center',
            '& .MuiGrid-item': {
              display: 'flex',
              justifyContent: 'center',
            },
          },

          // Top alignment
          '&.MuiGrid-alignTop': {
            alignItems: 'flex-start',
          },

          // Bottom alignment  
          '&.MuiGrid-alignBottom': {
            alignItems: 'flex-end',
          },

          // PERFORMANCE OPTIMIZATIONS

          // GPU acceleration for smooth scrolling with many grid items
          '&.MuiGrid-optimized': {
            willChange: 'transform',
            '& .MuiGrid-item': {
              willChange: 'transform',
              backfaceVisibility: 'hidden',
            },
          },

          // Dense layout for compact displays
          '&.MuiGrid-dense': {
            '& .MuiGrid-item': {
              '@media (max-width:599px)': {
                padding: '2px',
              },
              '@media (min-width:600px)': {
                padding: '4px',
              },
            },
          },
        },

        // Enhanced item styles
        item: {
          // Base item styles
          boxSizing: 'border-box',
          
          // Responsive item behaviors
          '&.MuiGrid-responsive': {
            // Auto-responsive behavior
            '@media (max-width:599px)': {
              width: '100%',
              flexBasis: '100%',
              maxWidth: '100%',
            },
          },

          // Flex item utilities
          '&.MuiGrid-flex': {
            display: 'flex',
            
            '&.MuiGrid-flexColumn': {
              flexDirection: 'column',
            },
            
            '&.MuiGrid-flexCenter': {
              justifyContent: 'center',
              alignItems: 'center',
            },
            
            '&.MuiGrid-flexBetween': {
              justifyContent: 'space-between',
            },
            
            '&.MuiGrid-flexAround': {
              justifyContent: 'space-around',
            },
          },

          // Aspect ratio utilities for consistent sizing
          '&.MuiGrid-aspectSquare': {
            aspectRatio: '1 / 1',
          },
          
          '&.MuiGrid-aspectVideo': {
            aspectRatio: '16 / 9',
          },
          
          '&.MuiGrid-aspectCard': {
            aspectRatio: '4 / 3',
          },

          // Order utilities for responsive reordering
          '&.MuiGrid-orderFirst': {
            order: -1,
          },
          
          '&.MuiGrid-orderLast': {
            order: 999,
          },
          
          '@media (max-width:599px)': {
            '&.MuiGrid-orderFirstMobile': {
              order: -1,
            },
            '&.MuiGrid-orderLastMobile': {
              order: 999,
            },
          },
        },
      },
    },

    // Paper components with responsive padding and form section utilities
    MuiPaper: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            margin: '8px',
            padding: '12px',
            borderRadius: '8px',
          },
          // Tablet-specific Paper optimizations (sm: 600-899px)
          '@media (min-width:600px) and (max-width:899px)': {
            margin: '12px',
            padding: '20px',
            borderRadius: '12px',
            // Enhanced shadows for tablet depth perception
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
          // Medium tablet and up (md: 900-1199px)
          '@media (min-width:900px) and (max-width:1199px)': {
            margin: '16px',
            padding: '28px',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            margin: '20px',
            padding: '32px',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
            transition: 'box-shadow 0.2s ease-in-out',
            // Professional desktop styling
            border: '1px solid rgba(0, 0, 0, 0.05)',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
            },
            // Enhanced form sections for desktop
            '&[data-form-section="true"]': {
              padding: '40px',
              marginBottom: '32px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
            },
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            margin: '24px',
            padding: '40px',
            borderRadius: '16px',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            // Premium desktop styling for ultra-wide screens
            '&:hover': {
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
            },
            // Enhanced form sections for ultra-wide screens
            '&[data-form-section="true"]': {
              padding: '48px',
              marginBottom: '40px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '20px',
            },
                     },
          
          // Form section papers with enhanced spacing
          '&[data-form-section="true"]': {
            marginBottom: '24px',
            '@media (max-width:599px)': {
              marginBottom: '16px',
              padding: '16px',
              marginLeft: '0',
              marginRight: '0',
            },
            // Tablet form sections with optimized spacing
            '@media (min-width:600px) and (max-width:899px)': {
              padding: '20px',
              marginBottom: '20px',
            },
            '@media (min-width:900px) and (max-width:1199px)': {
              padding: '28px',
              marginBottom: '28px',
            },
            '@media (min-width:1200px)': {
              padding: '32px',
            },
          },
        },
      },
    },

    // Form controls optimized for mobile
    MuiTextField: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            '& .MuiInputBase-root': {
              fontSize: '1rem', // Prevent zoom on iOS
              minHeight: '48px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem',
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.75rem',
            },
          },
        },
      },
    },

    // Form control labels with better mobile spacing
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            marginLeft: 0,
            marginRight: 0,
            '& .MuiCheckbox-root, & .MuiRadio-root': {
              padding: '12px',
            },
            '& .MuiTypography-root': {
              fontSize: '0.875rem',
            },
          },
        },
      },
    },

    // Radio and checkbox with larger touch targets
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            padding: '12px',
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem',
            },
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            padding: '12px',
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem',
            },
          },
        },
      },
    },

    // Dialog optimized for mobile and tablet
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width:599px)': {
            margin: '8px',
            width: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 16px)',
          },
          // Tablet-specific dialog sizing (sm: 600-899px)
          '@media (min-width:600px) and (max-width:899px)': {
            margin: '32px',
            maxWidth: '480px',
            width: 'calc(100% - 64px)',
            maxHeight: 'calc(100% - 64px)',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          },
          // Medium tablet and up (md: 900-1199px)
          '@media (min-width:900px) and (max-width:1199px)': {
            margin: '48px',
            maxWidth: '600px',
            width: 'calc(100% - 96px)',
            maxHeight: 'calc(100% - 96px)',
            borderRadius: '20px',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)',
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            margin: '64px',
            maxWidth: '720px',
            width: 'calc(100% - 128px)',
            maxHeight: 'calc(100% - 128px)',
            borderRadius: '16px',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            // Professional desktop dialog styling
            backdropFilter: 'blur(4px)',
            // Enhanced elevation and depth
            '&.MuiDialog-paperFullWidth': {
              maxWidth: '960px',
            },
            // Special styling for form dialogs
            '&[data-dialog-type="form"]': {
              maxWidth: '800px',
              minHeight: '400px',
            },
            // Alert dialogs with compact sizing
            '&[data-dialog-type="alert"]': {
              maxWidth: '480px',
              minWidth: '400px',
            },
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            margin: '80px',
            maxWidth: '800px',
            width: 'calc(100% - 160px)',
            maxHeight: 'calc(100% - 160px)',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            // Premium dialog styling for ultra-wide screens
            backdropFilter: 'blur(8px)',
            // Enhanced sizing for different dialog types
            '&.MuiDialog-paperFullWidth': {
              maxWidth: '1200px',
            },
            '&[data-dialog-type="form"]': {
              maxWidth: '1000px',
              minHeight: '500px',
            },
            '&[data-dialog-type="alert"]': {
              maxWidth: '520px',
              minWidth: '440px',
            },
          },
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            padding: '16px',
            fontSize: '1.125rem',
          },
          // Tablet-specific dialog title styling
          '@media (min-width:600px) and (max-width:899px)': {
            padding: '20px 20px 16px',
            fontSize: '1.25rem',
            fontWeight: 600,
          },
          '@media (min-width:900px) and (max-width:1199px)': {
            padding: '24px 24px 20px',
            fontSize: '1.375rem',
            fontWeight: 600,
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            padding: '32px 32px 24px',
            fontSize: '1.5rem',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            // Enhanced typography for desktop
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            marginBottom: '8px',
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            padding: '40px 40px 32px',
            fontSize: '1.625rem',
            fontWeight: 600,
            letterSpacing: '-0.025em',
            lineHeight: 1.15,
            // Premium typography for ultra-wide screens
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
            marginBottom: '12px',
          },
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            padding: '8px 16px',
          },
          // Tablet-specific dialog content spacing
          '@media (min-width:600px) and (max-width:899px)': {
            padding: '0 20px 16px',
          },
          '@media (min-width:900px) and (max-width:1199px)': {
            padding: '0 24px 20px',
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            padding: '0 32px 24px',
            fontSize: '1rem',
            lineHeight: 1.6,
            // Enhanced readability for desktop
            '& .MuiTypography-body1': {
              fontSize: '1rem',
              lineHeight: 1.6,
            },
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            padding: '0 40px 32px',
            fontSize: '1.0625rem',
            lineHeight: 1.65,
            // Premium content styling for ultra-wide screens
            '& .MuiTypography-body1': {
              fontSize: '1.0625rem',
              lineHeight: 1.65,
            },
          },
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            padding: '16px',
            flexDirection: 'column',
            '& .MuiButton-root': {
              width: '100%',
              marginLeft: 0,
              marginBottom: '8px',
              '&:last-child': {
                marginBottom: 0,
              },
            },
          },
          // Tablet-specific dialog actions (sm: 600-899px)
          '@media (min-width:600px) and (max-width:899px)': {
            padding: '16px 20px 20px',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: '12px',
            '& .MuiButton-root': {
              minWidth: '120px',
              marginLeft: 0,
            },
          },
          // Medium tablet dialog actions (md: 900-1199px)
          '@media (min-width:900px) and (max-width:1199px)': {
            padding: '20px 24px 24px',
            gap: '16px',
            '& .MuiButton-root': {
              minWidth: '140px',
            },
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            padding: '24px 32px 32px',
            gap: '20px',
            justifyContent: 'flex-end',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            marginTop: '16px',
            paddingTop: '24px',
            '& .MuiButton-root': {
              minWidth: '160px',
              height: '44px',
              fontSize: '1rem',
              fontWeight: 500,
            },
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            padding: '32px 40px 40px',
            gap: '24px',
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            marginTop: '20px',
            paddingTop: '32px',
            '& .MuiButton-root': {
              minWidth: '180px',
              height: '48px',
              fontSize: '1.0625rem',
              fontWeight: 500,
            },
          },
        },
      },
    },

    // Card components with tablet-specific spacing and hover effects
    MuiCard: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            borderRadius: '8px',
            margin: '8px 0',
          },
          // Tablet-specific card styling (sm: 600-899px)
          '@media (min-width:600px) and (max-width:899px)': {
            borderRadius: '12px',
            margin: '12px 0',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
            },
          },
          // Medium tablet card styling (md: 900-1199px)
          '@media (min-width:900px) and (max-width:1199px)': {
            borderRadius: '16px',
            margin: '16px 0',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
            },
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            borderRadius: '12px',
            margin: '20px 0',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            // Professional desktop card effects
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
              borderColor: 'rgba(25, 118, 210, 0.15)',
            },
            '&:active': {
              transform: 'translateY(-2px)',
            },
            // Enhanced clickable cards
            '&.MuiCard-clickable': {
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 16px 40px rgba(0, 0, 0, 0.15)',
                borderColor: 'rgba(25, 118, 210, 0.2)',
              },
            },
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            borderRadius: '16px',
            margin: '24px 0',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            // Premium card styling for ultra-wide screens
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.15)',
              borderColor: 'rgba(25, 118, 210, 0.2)',
            },
            // Enhanced spacing and effects for premium experience
            '&.MuiCard-clickable': {
              '&:hover': {
                transform: 'translateY(-10px)',
                boxShadow: '0 20px 48px rgba(0, 0, 0, 0.18)',
                borderColor: 'rgba(25, 118, 210, 0.25)',
              },
            },
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            padding: '12px',
          },
          // Tablet-specific card content spacing
          '@media (min-width:600px) and (max-width:899px)': {
            padding: '16px',
          },
          '@media (min-width:900px) and (max-width:1199px)': {
            padding: '20px',
          },
          // DESKTOP OPTIMIZATION - Large screens (lg: 1200-1535px)
          '@media (min-width:1200px) and (max-width:1535px)': {
            padding: '24px',
            // Enhanced content spacing for desktop
            '&:last-child': {
              paddingBottom: '24px',
            },
          },
          // DESKTOP OPTIMIZATION - Extra large screens (xl: 1536px+)
          '@media (min-width:1536px)': {
            padding: '32px',
            // Premium content spacing for ultra-wide screens
            '&:last-child': {
              paddingBottom: '32px',
            },
          },
        },
      },
    },

    // Chip components with tablet-specific sizing
    MuiChip: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            height: '28px',
            fontSize: '0.75rem',
            padding: '0 8px',
          },
          // Tablet-specific chip sizing (sm: 600-899px)
          '@media (min-width:600px) and (max-width:899px)': {
            height: '30px',
            fontSize: '0.8125rem',
            padding: '0 10px',
            borderRadius: '15px',
          },
          // Medium tablet chip sizing (md: 900-1199px)
          '@media (min-width:900px) and (max-width:1199px)': {
            height: '32px',
            fontSize: '0.875rem',
            padding: '0 12px',
            borderRadius: '16px',
          },
        },
      },
    },



    // List components with tablet-specific spacing optimization
    MuiListItem: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            paddingTop: '12px',
            paddingBottom: '12px',
          },
          // Tablet-specific list item spacing (sm: 600-899px)
          '@media (min-width:600px) and (max-width:899px)': {
            paddingTop: '14px',
            paddingBottom: '14px',
            paddingLeft: '20px',
            paddingRight: '20px',
            borderRadius: '8px',
            margin: '2px 8px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            },
          },
          // Medium tablet list item styling (md: 900-1199px)
          '@media (min-width:900px) and (max-width:1199px)': {
            paddingTop: '16px',
            paddingBottom: '16px',
            paddingLeft: '24px',
            paddingRight: '24px',
            borderRadius: '10px',
            margin: '4px 12px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              transform: 'translateX(4px)',
              transition: 'all 0.15s ease-in-out',
            },
          },
        },
      },
    },

    // Table responsive patterns
    MuiTable: {
      styleOverrides: {
        root: {
          // Base responsive table behavior
          '&.MuiTable-responsive': {
            // Mobile-first table optimization (xs: 320-599px)
            '@media (max-width:599px)': {
              fontSize: '0.75rem',
              '& .MuiTableCell-root': {
                padding: '8px 4px',
                fontSize: '0.75rem',
                // Hide non-essential columns on mobile
                '&[data-column-priority="low"]': {
                  display: 'none',
                },
                '&[data-column-priority="medium"]': {
                  display: 'none',
                },
                // Keep only high priority and essential columns
                '&[data-column-priority="high"], &[data-column-priority="essential"]': {
                  display: 'table-cell',
                },
              },
              '& .MuiTableCell-head': {
                fontWeight: 600,
                fontSize: '0.75rem',
                lineHeight: 1.2,
              },
              // Mobile action buttons optimization
              '& .table-actions': {
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                alignItems: 'flex-start',
                '& .MuiIconButton-root': {
                  minWidth: '44px',
                  minHeight: '44px',
                  padding: '10px',
                },
              },
            },
            
            // Small tablet optimization (sm: 600-899px)
            '@media (min-width:600px) and (max-width:899px)': {
              fontSize: '0.875rem',
              '& .MuiTableCell-root': {
                padding: '12px 8px',
                fontSize: '0.875rem',
                // Show medium priority columns on tablet
                '&[data-column-priority="low"]': {
                  display: 'none',
                },
                '&[data-column-priority="medium"], &[data-column-priority="high"], &[data-column-priority="essential"]': {
                  display: 'table-cell',
                },
              },
              '& .MuiTableCell-head': {
                fontWeight: 600,
                fontSize: '0.875rem',
              },
              // Tablet action buttons
              '& .table-actions': {
                display: 'flex',
                gap: '4px',
                alignItems: 'center',
                '& .MuiIconButton-root': {
                  minWidth: '40px',
                  minHeight: '40px',
                  padding: '8px',
                },
              },
            },
            
            // Desktop and larger (md: 900px+)
            '@media (min-width:900px)': {
              fontSize: '0.875rem',
              '& .MuiTableCell-root': {
                padding: '16px',
                fontSize: '0.875rem',
                // Show all columns on desktop
                '&[data-column-priority]': {
                  display: 'table-cell',
                },
              },
              '& .MuiTableCell-head': {
                fontWeight: 600,
                fontSize: '0.875rem',
              },
              // Desktop action buttons
              '& .table-actions': {
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                '& .MuiIconButton-root': {
                  minWidth: '36px',
                  minHeight: '36px',
                  padding: '6px',
                },
              },
            },
          },
          
          // Compact responsive table variant for dense data
          '&.MuiTable-compact': {
            '@media (max-width:599px)': {
              '& .MuiTableCell-root': {
                padding: '4px 2px',
                fontSize: '0.7rem',
                lineHeight: 1.2,
              },
              '& .MuiTableCell-head': {
                fontSize: '0.7rem',
                fontWeight: 600,
              },
            },
          },
          
          // Horizontal scroll table for overflow handling
          '&.MuiTable-scroll': {
            '@media (max-width:899px)': {
              '& .MuiTableContainer-root': {
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '3px',
                },
              },
              minWidth: '600px', // Minimum table width for readability
            },
          },
        },
      },
    },

    // TableContainer responsive patterns
    MuiTableContainer: {
      styleOverrides: {
        root: {
          // Mobile-optimized container
          '@media (max-width:599px)': {
            '&.responsive-scroll': {
              overflowX: 'auto',
              overflowY: 'visible',
              '&::-webkit-scrollbar': {
                height: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: '2px',
              },
            },
          },
          
          // Enhanced scroll behavior for tablets
          '@media (min-width:600px) and (max-width:899px)': {
            '&.responsive-scroll': {
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '6px',
              },
            },
          },
        },
      },
    },

    // TableCell responsive behavior
    MuiTableCell: {
      styleOverrides: {
        root: {
          // Responsive text truncation
          '&.truncate-mobile': {
            '@media (max-width:599px)': {
              maxWidth: '80px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '@media (min-width:600px) and (max-width:899px)': {
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
          
          // Responsive cell variants
          '&.cell-name': {
            '@media (max-width:599px)': {
              minWidth: '100px',
              fontWeight: 600,
            },
          },
          
          '&.cell-status': {
            '@media (max-width:599px)': {
              minWidth: '80px',
            },
          },
          
          '&.cell-actions': {
            '@media (max-width:599px)': {
              minWidth: '60px',
              padding: '4px 2px',
            },
          },
          
          '&.cell-date': {
            '@media (max-width:599px)': {
              fontSize: '0.7rem',
              minWidth: '70px',
            },
          },
        },
      },
    },

    // Menu items with touch-friendly spacing
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            minHeight: '48px',
            padding: '12px 16px',
          },
        },
      },
    },



    // Alert with mobile-friendly styling
    MuiAlert: {
      styleOverrides: {
        root: {
          '@media (max-width:599px)': {
            fontSize: '0.875rem',
            padding: '12px 16px',
            '& .MuiAlert-icon': {
              fontSize: '1.25rem',
            },
          },
        },
      },
    },

    // Enhanced Form Layout Utilities for Responsive Design
    // These utilities provide specialized responsive patterns for different form types
    
    MuiFormControl: {
      styleOverrides: {
        root: {
          // Enhanced form control with responsive sizing
          '@media (max-width:599px)': {
            marginBottom: '16px',
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem',
              transform: 'translate(14px, 16px) scale(1)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
              },
            },
          },
          
          // Form section utilities for grouping related fields
          '&.MuiFormControl-sectionHeader': {
            marginTop: '24px',
            marginBottom: '16px',
            '@media (max-width:599px)': {
              marginTop: '16px',
              marginBottom: '12px',
            },
          },
          
          // Compact form variant for dense layouts
          '&.MuiFormControl-compact': {
            '@media (max-width:599px)': {
              marginBottom: '8px',
              '& .MuiInputBase-root': {
                minHeight: '40px',
              },
            },
          },
        },
      },
    },

    // Enhanced Grid specifically for form layouts
    MuiGrid2: {
      styleOverrides: {
        root: {
          // Form field grouping patterns
          '&[data-form-section="header"]': {
            marginBottom: '24px',
            '@media (max-width:599px)': {
              marginBottom: '16px',
            },
          },
          
          '&[data-form-section="main"]': {
            marginBottom: '20px',
            '@media (max-width:599px)': {
              marginBottom: '16px',
            },
          },
          
          '&[data-form-section="actions"]': {
            marginTop: '32px',
            '@media (max-width:599px)': {
              marginTop: '24px',
            },
          },
          
          // Form field width patterns for different content types
          '&[data-field-type="name"]': {
            // Name fields: 2-column on tablet+, 1-column on mobile
            '@media (max-width:599px)': {
              width: '100%',
            },
            '@media (min-width:600px)': {
              width: '50%',
            },
          },
          
          '&[data-field-type="contact"]': {
            // Contact fields: 2-column on tablet+, 1-column on mobile
            '@media (max-width:599px)': {
              width: '100%',
            },
            '@media (min-width:600px)': {
              width: '50%',
            },
          },
          
          '&[data-field-type="address-street"]': {
            // Street address: full width on all sizes
            width: '100%',
          },
          
          '&[data-field-type="address-detail"]': {
            // City/State/Zip: 3-column on desktop, 2-column on tablet, 1-column on mobile
            '@media (max-width:599px)': {
              width: '100%',
            },
            '@media (min-width:600px) and (max-width:899px)': {
              width: '50%',
            },
            '@media (min-width:900px)': {
              width: '33.333%',
            },
          },
          
          '&[data-field-type="date"]': {
            // Date fields: 2-column on tablet+, 1-column on mobile
            '@media (max-width:599px)': {
              width: '100%',
            },
            '@media (min-width:600px)': {
              width: '50%',
            },
          },
          
          '&[data-field-type="select"]': {
            // Select fields: 2-column on tablet+, 1-column on mobile
            '@media (max-width:599px)': {
              width: '100%',
            },
            '@media (min-width:600px)': {
              width: '50%',
            },
          },
          
          '&[data-field-type="textarea"]': {
            // Text areas: full width on all sizes
            width: '100%',
          },
          
          '&[data-field-type="autocomplete"]': {
            // Autocomplete fields: full width on mobile, can be 2-column on larger screens
            '@media (max-width:599px)': {
              width: '100%',
            },
            '@media (min-width:600px)': {
              width: '100%', // Usually complex, better full width
            },
          },
          
          '&[data-field-type="radio-group"]': {
            // Radio groups: full width with responsive internal layout
            width: '100%',
            '& .MuiFormGroup-root': {
              '@media (max-width:599px)': {
                flexDirection: 'column',
                gap: '8px',
              },
              '@media (min-width:600px)': {
                flexDirection: 'row',
                gap: '16px',
                flexWrap: 'wrap',
              },
            },
          },
          
          '&[data-field-type="checkbox-group"]': {
            // Checkbox groups: responsive layout similar to radio groups
            width: '100%',
            '& .MuiFormGroup-root': {
              '@media (max-width:599px)': {
                flexDirection: 'column',
                gap: '8px',
              },
              '@media (min-width:600px)': {
                flexDirection: 'row',
                gap: '16px',
                flexWrap: 'wrap',
              },
            },
          },
        },
      },
    },



    // Enhanced Typography for form section headers
    MuiTypography: {
      styleOverrides: {
        h6: {
          // Form section headers
          '&[data-form-header="true"]': {
            marginBottom: '16px',
            paddingBottom: '8px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            '@media (max-width:599px)': {
              fontSize: '1rem',
              marginBottom: '12px',
            },
            '@media (min-width:600px)': {
              fontSize: '1.125rem',
            },
          },
        },
      },
    },

    // Enhanced Autocomplete for better mobile experience and fixed dropdown visibility
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          // Fix label truncation in Autocomplete components
          '& .MuiInputLabel-root': {
            // Ensure labels don't get truncated
            fontSize: '1rem',
            whiteSpace: 'nowrap',
            overflow: 'visible',
            textOverflow: 'clip',
            transform: 'translate(14px, 16px) scale(1)',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
              fontSize: '1rem', // Maintain readable size when shrunk
            },
          },
          '& .MuiInputBase-root': {
            // Ensure proper spacing for non-truncated labels
            paddingTop: '16px',
          },
          '@media (max-width:599px)': {
            '& .MuiInputBase-root': {
              fontSize: '1rem', // Prevent zoom on iOS
              minHeight: '48px',
              paddingTop: '16px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem', // Slightly smaller on mobile but still readable
              whiteSpace: 'nowrap',
              overflow: 'visible',
              textOverflow: 'clip',
              transform: 'translate(14px, 16px) scale(1)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
                fontSize: '0.875rem',
              },
            },
            '& .MuiAutocomplete-tag': {
              fontSize: '0.75rem',
              height: '28px',
            },
          },
        },
        popper: {
          // Ensure dropdown appears above dialogs and other content
          zIndex: 1400,
          '& .MuiPaper-root': {
            // Override any conflicting Paper styles for dropdown
            margin: '4px 0',
            padding: '0',
            borderRadius: '4px',
            boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
            // Reset any form section styling that might interfere
            '&[data-form-section]': {
              marginBottom: '0',
              padding: '0',
            },
          },
        },
        listbox: {
          // Enhanced listbox styling for better visibility
          maxHeight: '220px', // Limit height for mobile
          padding: '8px 0',
          backgroundColor: '#ffffff',
          '@media (max-width:599px)': {
            fontSize: '0.875rem',
            maxHeight: '200px', // Slightly smaller on mobile
            '& .MuiAutocomplete-option': {
              minHeight: '48px',
              padding: '12px 16px',
              // Ensure proper contrast and visibility
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '&[aria-selected="true"]': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.16)',
                },
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
              },
            },
          },
          '@media (min-width:600px)': {
            '& .MuiAutocomplete-option': {
              minHeight: '40px',
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '&[aria-selected="true"]': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.16)',
                },
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
              },
            },
          },
        },
        option: {
          // Base option styling
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'rgba(25, 118, 210, 0.12) !important',
            fontWeight: 500,
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(25, 118, 210, 0.12) !important',
          },
        },
        paper: {
          // Ensure the Paper component doesn't inherit conflicting styles
          margin: '4px 0 !important',
          padding: '0 !important',
          borderRadius: '4px !important',
          // Override any data-form-section styling
          '&[data-form-section="true"]': {
            marginBottom: '0 !important',
            padding: '0 !important',
            marginLeft: '0 !important',
            marginRight: '0 !important',
          },
        },
      },
    },

    // Enhanced FormGroup for responsive radio/checkbox layouts
    MuiFormGroup: {
      styleOverrides: {
        root: {
          // Responsive form group layout
          '&.MuiFormGroup-responsive': {
            '@media (max-width:599px)': {
              flexDirection: 'column',
              gap: '8px',
              '& .MuiFormControlLabel-root': {
                marginLeft: 0,
                marginRight: 0,
              },
            },
            '@media (min-width:600px)': {
              flexDirection: 'row',
              gap: '16px',
              flexWrap: 'wrap',
              '& .MuiFormControlLabel-root': {
                marginRight: '16px',
              },
            },
          },
        },
      },
    },
  },
});

export default theme; 